import React, { useEffect, useState } from 'react';
import useAuth from '../hooks/useAuth';
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Typography,
    useTheme,
    Chip,
    Alert,
    Autocomplete,
    useMediaQuery,
    CardContent,
    Divider,
    Avatar
} from '@mui/material';
import { Add, Edit, Delete, Visibility, Event, Person, MedicalServices, AccessTime, CalendarToday } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import useAxiosPrivate from '../hooks/useAxiosPrivate';

const AppointmentsPage = () => {
    const { auth } = useAuth();
    const axiosPrivate = useAxiosPrivate();
    const { t } = useTranslation('global');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { id } = useParams(); // elderly_id (opzionale)
    const navigate = useNavigate();

    const [appointments, setAppointments] = useState([]);
    const [elderlyList, setElderlyList] = useState([]);
    const [doctorsList, setDoctorsList] = useState([]);
    const [familyMembersList, setFamilyMembersList] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        elderly_id: id || '',
        doctor_id: '',
        family_member_id: '',
        external_doctor_name: '',
        external_family_member_name: '',
        appointment_date: '',
        start_time: '',
        end_time: '',
        appointment_type: 'visita_medica',
        description: '',
        status: 'scheduled',
        notes: ''
    });

    // Colori tabella in base al tema
    const getTableColors = () => {
        if (theme.palette.mode === 'dark') {
            return {
                header: theme.palette.background.paper,
                rowEven: theme.palette.background.default,
                rowOdd: theme.palette.action.hover,
                hover: theme.palette.action.selected,
                text: theme.palette.text.primary,
                headerText: theme.palette.text.primary,
                border: theme.palette.divider
            };
        } else if (theme.palette.primary.main === '#d2a679') {
            return {
                header: '#8d6e63',
                rowEven: '#eee0cb',
                rowOdd: '#d7cdb8',
                hover: '#c8b393',
                text: '#5d4037',
                headerText: '#ffffff',
                border: '#a1887f'
            };
        } else {
            return {
                header: theme.palette.primary.main,
                rowEven: theme.palette.background.paper,
                rowOdd: theme.palette.grey[100],
                hover: theme.palette.grey[200],
                text: '#000000',
                headerText: '#ffffff',
                border: theme.palette.divider
            };
        }
    };

    const tableColors = getTableColors();

    // Tipi di appuntamento
    const appointmentTypes = [
        { value: 'visita_medica', label: t('appointments.types.medical', 'Visita Medica') },
        { value: 'visita_parenti', label: t('appointments.types.family', 'Visita Parenti') },
        { value: 'ricovero', label: t('appointments.types.hospitalization', 'Ricovero') },
        { value: 'vacanza', label: t('appointments.types.vacation', 'Vacanza') },
        { value: 'altro', label: t('appointments.types.other', 'Altro') }
    ];

    // Stati appuntamento
    const statusOptions = [
        { value: 'scheduled', label: t('appointments.status.scheduled', 'Programmato'), color: 'primary' },
        { value: 'completed', label: t('appointments.status.completed', 'Completato'), color: 'success' },
        { value: 'cancelled', label: t('appointments.status.cancelled', 'Annullato'), color: 'error' },
        { value: 'in_progress', label: t('appointments.status.in_progress', 'In Corso'), color: 'warning' }
    ];

    // Carica dati iniziali
    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        loadAppointments();
        loadElderlyList();
        loadDoctorsList();
        if (id) {
            loadFamilyMembers(id);
        }
    }, [id, auth?.accessToken]);

    const loadAppointments = async () => {
        try {
            const url = id ? `/appointments/elderly/${id}` : '/appointments';
            const response = await axiosPrivate.get(url);
            setAppointments(response.data);
        } catch (err) {
            console.error('Errore caricamento appuntamenti:', err);
            setError('Errore nel caricamento degli appuntamenti');
        }
    };

    const loadElderlyList = async () => {
        try {
            const response = await axiosPrivate.get('/elderly');
            setElderlyList(response.data);
        } catch (err) {
            console.error('Errore caricamento anziani:', err);
        }
    };

    const loadDoctorsList = async () => {
        try {
            const response = await axiosPrivate.get('/doctors');
            setDoctorsList(response.data);
        } catch (err) {
            console.error('Errore caricamento dottori:', err);
        }
    };

    const loadFamilyMembers = async (elderlyId) => {
        try {
            const response = await axiosPrivate.get(`/family-members/${elderlyId}`);
            setFamilyMembersList(response.data);
        } catch (err) {
            console.error('Errore caricamento familiari:', err);
            setFamilyMembersList([]);
        }
    };

    // Gestione form
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Se cambia l'anziano, ricarica i familiari
        if (name === 'elderly_id' && value) {
            loadFamilyMembers(value);
        }

        // Reset dei campi correlati in base al tipo
        if (name === 'appointment_type') {
            if (value === 'visita_medica') {
                setFormData(prev => ({ ...prev, family_member_id: '' }));
            } else if (value === 'visita_parenti') {
                setFormData(prev => ({ ...prev, doctor_id: '' }));
                // Carica i familiari se è già selezionato un anziano
                if (formData.elderly_id) {
                    loadFamilyMembers(formData.elderly_id);
                }
            } else if (value === 'ricovero' || value === 'vacanza') {
                setFormData(prev => ({ ...prev, doctor_id: '', family_member_id: '' }));
            }
        }
    };

    const handleOpenDialog = (appointment = null) => {
        if (appointment) {
            setEditingId(appointment.appointment_id);
            setFormData({
                elderly_id: appointment.elderly_id,
                doctor_id: appointment.doctor_id || '',
                family_member_id: appointment.family_member_id || '',
                external_doctor_name: appointment.external_doctor_name || '',
                external_family_member_name: appointment.external_family_member_name || '',
                appointment_date: appointment.appointment_date,
                start_time: appointment.start_time,
                end_time: appointment.end_time,
                appointment_type: appointment.appointment_type,
                description: appointment.description || '',
                status: appointment.status,
                notes: appointment.notes || ''
            });
            if (appointment.elderly_id) {
                loadFamilyMembers(appointment.elderly_id);
            }
        } else {
            setEditingId(null);
            setFormData({
                elderly_id: id || '',
                doctor_id: '',
                family_member_id: '',
                external_doctor_name: '',
                external_family_member_name: '',
                appointment_date: '',
                start_time: '',
                end_time: '',
                appointment_type: 'visita_medica',
                description: '',
                status: 'scheduled',
                notes: ''
            });
        }
        setOpenDialog(true);
        setError('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingId(null);
        setError('');
    };

    const validateForm = () => {
        if (!formData.elderly_id) {
            setError('Seleziona un anziano');
            return false;
        }
        if (!formData.appointment_date) {
            setError('Inserisci la data dell\'appuntamento');
            return false;
        }
        if (!formData.start_time) {
            setError('Inserisci l\'ora di inizio');
            return false;
        }
        if (!formData.end_time) {
            setError('Inserisci l\'ora di fine');
            return false;
        }
        if (formData.start_time >= formData.end_time) {
            setError('L\'ora di fine deve essere successiva all\'ora di inizio');
            return false;
        }
        if (formData.appointment_type === 'visita_medica' && !formData.doctor_id && !formData.external_doctor_name) {
            setError('Seleziona un dottore o inserisci il nome del dottore esterno');
            return false;
        }
        if (formData.appointment_type === 'visita_parenti' && !formData.family_member_id && !formData.external_family_member_name) {
            setError('Seleziona un familiare o inserisci il nome del parente esterno');
            return false;
        }
        return true;
    };

    const handleSaveAppointment = async () => {
        if (!validateForm()) return;

        try {
            if (editingId) {
                await axiosPrivate.patch(`/appointments/${editingId}`, formData);
            } else {
                await axiosPrivate.post('/appointments', formData);
            }
            loadAppointments();
            handleCloseDialog();
        } catch (err) {
            console.error('Errore salvataggio appuntamento:', err);
            setError(err.response?.data?.error || 'Errore nel salvataggio dell\'appuntamento');
        }
    };

    const handleDeleteAppointment = async (appointmentId) => {
        if (window.confirm(t('appointments.confirmDelete', 'Sei sicuro di voler eliminare questo appuntamento?'))) {
            try {
                await axiosPrivate.delete(`/appointments/${appointmentId}`);
                loadAppointments();
            } catch (err) {
                console.error('Errore eliminazione appuntamento:', err);
                setError('Errore nell\'eliminazione dell\'appuntamento');
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT');
    };

    const formatTime = (timeString) => {
        if (!timeString) return '-';
        return timeString.substring(0, 5); // HH:MM
    };

    const getTypeLabel = (type) => {
        const typeObj = appointmentTypes.find(t => t.value === type);
        return typeObj ? typeObj.label : type;
    };

    const getStatusChip = (status) => {
        const statusObj = statusOptions.find(s => s.value === status);
        return (
            <Chip 
                label={statusObj?.label || status} 
                color={statusObj?.color || 'default'}
                size="small"
            />
        );
    };

    // Componente Card per visualizzazione mobile
    const AppointmentCardMobile = ({ appointment }) => (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday fontSize="small" color="primary" />
                            {formatDate(appointment.appointment_date)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <AccessTime fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {formatTime(appointment.start_time)} - {formatTime(appointment.end_time)}
                            </Typography>
                        </Box>
                    </Box>
                    {getStatusChip(appointment.status)}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Tipo:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getTypeLabel(appointment.appointment_type)}
                    </Typography>
                </Box>

                {!id && (
                    <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">Anziano:</Typography>
                        <Typography variant="body2">
                            {appointment.elderly_name} {appointment.elderly_surname}
                            {appointment.n_room && ` - Stanza ${appointment.n_room}`}
                        </Typography>
                    </Box>
                )}

                {(appointment.doctor_id || appointment.external_doctor_name || appointment.family_member_id || appointment.external_family_member_name) && (
                    <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">Con:</Typography>
                        {appointment.doctor_id && (
                            <Typography variant="body2">
                                <MedicalServices fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                Dr. {appointment.doctor_name} {appointment.doctor_surname}
                                {appointment.doctor_specialization && ` - ${appointment.doctor_specialization}`}
                            </Typography>
                        )}
                        {appointment.external_doctor_name && !appointment.doctor_id && (
                            <Typography variant="body2">
                                <MedicalServices fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                {appointment.external_doctor_name} (Esterno)
                            </Typography>
                        )}
                        {appointment.family_member_id && (
                            <Typography variant="body2">
                                <Person fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                {appointment.family_members_name} {appointment.family_members_surname}
                                {appointment.degree_of_kinship && ` (${appointment.degree_of_kinship})`}
                            </Typography>
                        )}
                        {appointment.external_family_member_name && !appointment.family_member_id && (
                            <Typography variant="body2">
                                <Person fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                {appointment.external_family_member_name} (Esterno)
                            </Typography>
                        )}
                    </Box>
                )}

                {appointment.description && (
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">Descrizione:</Typography>
                        <Typography variant="body2">{appointment.description}</Typography>
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleOpenDialog(appointment)}
                        fullWidth
                    >
                        Modifica
                    </Button>
                    <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteAppointment(appointment.appointment_id)}
                    >
                        <Delete />
                    </IconButton>
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <Box component="main" sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 } }}>
            <Toolbar />
            
            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mb: 3 
            }}>
                <Typography variant={isMobile ? "h5" : "h4"}>
                    <Event sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {id 
                        ? t('appointments.titleElderly', 'Appuntamenti Anziano')
                        : t('appointments.title', 'Gestione Appuntamenti')
                    }
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                    fullWidth={isMobile}
                >
                    {t('appointments.add', 'Nuovo Appuntamento')}
                </Button>
            </Box>

            {error && !openDialog && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Visualizzazione Mobile - Card */}
            {isMobile ? (
                <Box>
                    {appointments.length === 0 ? (
                        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                            {t('appointments.noData', 'Nessun appuntamento trovato')}
                        </Typography>
                    ) : (
                        appointments.map((appointment) => (
                            <AppointmentCardMobile key={appointment.appointment_id} appointment={appointment} />
                        ))
                    )}
                </Box>
            ) : (
                /* Visualizzazione Desktop - Tabella */
                <Card>
                    <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: tableColors.header }}>
                                {!id && (
                                    <TableCell sx={{ color: tableColors.headerText, fontWeight: 'bold' }}>
                                        {t('appointments.elderly', 'Anziano')}
                                    </TableCell>
                                )}
                                <TableCell sx={{ color: tableColors.headerText, fontWeight: 'bold' }}>
                                    {t('appointments.date', 'Data')}
                                </TableCell>
                                <TableCell sx={{ color: tableColors.headerText, fontWeight: 'bold' }}>
                                    {t('appointments.time', 'Orario')}
                                </TableCell>
                                <TableCell sx={{ color: tableColors.headerText, fontWeight: 'bold' }}>
                                    {t('appointments.type', 'Tipo')}
                                </TableCell>
                                <TableCell sx={{ color: tableColors.headerText, fontWeight: 'bold' }}>
                                    {t('appointments.with', 'Con')}
                                </TableCell>
                                <TableCell sx={{ color: tableColors.headerText, fontWeight: 'bold' }}>
                                    {t('appointments.description', 'Descrizione')}
                                </TableCell>
                                <TableCell sx={{ color: tableColors.headerText, fontWeight: 'bold' }}>
                                    {t('appointments.statusLabel', 'Stato')}
                                </TableCell>
                                <TableCell sx={{ color: tableColors.headerText, fontWeight: 'bold' }}>
                                    {t('appointments.actions', 'Azioni')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {appointments.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={id ? 7 : 8} align="center">
                                        {t('appointments.noData', 'Nessun appuntamento trovato')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                appointments.map((appointment, index) => (
                                    <TableRow
                                        key={appointment.appointment_id}
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? tableColors.rowEven : tableColors.rowOdd,
                                            '&:hover': { backgroundColor: tableColors.hover }
                                        }}
                                    >
                                        {!id && (
                                            <TableCell sx={{ color: tableColors.text }}>
                                                {appointment.elderly_name} {appointment.elderly_surname}
                                                {appointment.n_room && ` (${appointment.n_room})`}
                                            </TableCell>
                                        )}
                                        <TableCell sx={{ color: tableColors.text }}>
                                            {formatDate(appointment.appointment_date)}
                                        </TableCell>
                                        <TableCell sx={{ color: tableColors.text }}>
                                            {formatTime(appointment.start_time)} - {formatTime(appointment.end_time)}
                                        </TableCell>
                                        <TableCell sx={{ color: tableColors.text }}>
                                            {getTypeLabel(appointment.appointment_type)}
                                        </TableCell>
                                        <TableCell sx={{ color: tableColors.text }}>
                                            {appointment.doctor_id && (
                                                <div>
                                                    Dr. {appointment.doctor_name} {appointment.doctor_surname}
                                                    {appointment.doctor_specialization && (
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            {appointment.doctor_specialization}
                                                        </Typography>
                                                    )}
                                                </div>
                                            )}
                                            {appointment.external_doctor_name && !appointment.doctor_id && (
                                                <div>
                                                    {appointment.external_doctor_name}
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        (Esterno)
                                                    </Typography>
                                                </div>
                                            )}
                                            {appointment.family_member_id && (
                                                <div>
                                                    {appointment.family_members_name} {appointment.family_members_surname}
                                                    {appointment.degree_of_kinship && (
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            ({appointment.degree_of_kinship})
                                                        </Typography>
                                                    )}
                                                </div>
                                            )}
                                            {appointment.external_family_member_name && !appointment.family_member_id && (
                                                <div>
                                                    {appointment.external_family_member_name}
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        (Esterno)
                                                    </Typography>
                                                </div>
                                            )}
                                            {!appointment.doctor_id && !appointment.family_member_id && !appointment.external_doctor_name && !appointment.external_family_member_name && '-'}
                                        </TableCell>
                                        <TableCell sx={{ color: tableColors.text }}>
                                            {appointment.description || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getStatusChip(appointment.status)}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(appointment)}
                                                sx={{ color: theme.palette.primary.main }}
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteAppointment(appointment.appointment_id)}
                                                sx={{ color: theme.palette.error.main }}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
            )}

            {/* Dialog per aggiunta/modifica */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingId 
                        ? t('appointments.edit', 'Modifica Appuntamento')
                        : t('appointments.add', 'Nuovo Appuntamento')
                    }
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* Anziano */}
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('appointments.elderly', 'Anziano')}</InputLabel>
                                <Select
                                    name="elderly_id"
                                    value={formData.elderly_id}
                                    onChange={handleChange}
                                    label={t('appointments.elderly', 'Anziano')}
                                    disabled={!!id}
                                >
                                    {elderlyList.map((elderly) => (
                                        <MenuItem key={elderly.elderly_id} value={elderly.elderly_id}>
                                            {elderly.name} {elderly.surname} {elderly.n_room && `- Stanza ${elderly.n_room}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Data */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                type="date"
                                name="appointment_date"
                                label={t('appointments.date', 'Data')}
                                value={formData.appointment_date}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        {/* Ora inizio */}
                        <Grid item xs={12} sm={3}>
                            <TextField
                                fullWidth
                                required
                                type="time"
                                name="start_time"
                                label={t('appointments.startTime', 'Ora Inizio')}
                                value={formData.start_time}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        {/* Ora fine */}
                        <Grid item xs={12} sm={3}>
                            <TextField
                                fullWidth
                                required
                                type="time"
                                name="end_time"
                                label={t('appointments.endTime', 'Ora Fine')}
                                value={formData.end_time}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        {/* Tipo appuntamento */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('appointments.type', 'Tipo')}</InputLabel>
                                <Select
                                    name="appointment_type"
                                    value={formData.appointment_type}
                                    onChange={handleChange}
                                    label={t('appointments.type', 'Tipo')}
                                >
                                    {appointmentTypes.map((type) => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Stato */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('appointments.statusLabel', 'Stato')}</InputLabel>
                                <Select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    label={t('appointments.statusLabel', 'Stato')}
                                >
                                    {statusOptions.map((status) => (
                                        <MenuItem key={status.value} value={status.value}>
                                            {status.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Dottore (solo per visita medica) */}
                        {formData.appointment_type === 'visita_medica' && (
                            <>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>{t('appointments.doctor', 'Dottore')}</InputLabel>
                                        <Select
                                            name="doctor_id"
                                            value={formData.doctor_id}
                                            onChange={handleChange}
                                            label={t('appointments.doctor', 'Dottore')}
                                        >
                                            <MenuItem value="">
                                                <em>Nessuno (usa dottore esterno)</em>
                                            </MenuItem>
                                            {doctorsList.map((doctor) => (
                                                <MenuItem key={doctor.doctor_id} value={doctor.doctor_id}>
                                                    Dr. {doctor.doctor_name} {doctor.doctor_surname}
                                                    {doctor.doctor_specialization && ` - ${doctor.doctor_specialization}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {!formData.doctor_id && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            required
                                            label={t('appointments.externalDoctor', 'Dottore esterno (non in anagrafica)')}
                                            name="external_doctor_name"
                                            value={formData.external_doctor_name}
                                            onChange={handleChange}
                                            placeholder="Es: Dr. Mario Rossi - Cardiologo"
                                            helperText="Inserisci il nome del dottore esterno per visite specialistiche occasionali"
                                        />
                                    </Grid>
                                )}
                            </>
                        )}

                        {/* Familiare (solo per visita parenti) */}
                        {formData.appointment_type === 'visita_parenti' && (
                            <>
                                <Grid item xs={12}>
                                    <FormControl fullWidth disabled={!formData.elderly_id}>
                                        <InputLabel>{t('appointments.familyMember', 'Familiare')}</InputLabel>
                                        <Select
                                            name="family_member_id"
                                            value={formData.family_member_id}
                                            onChange={handleChange}
                                            label={t('appointments.familyMember', 'Familiare')}
                                        >
                                            <MenuItem value="">
                                                <em>Nessuno (usa parente esterno)</em>
                                            </MenuItem>
                                            {!formData.elderly_id && (
                                                <MenuItem value="" disabled>
                                                    Seleziona prima un anziano
                                                </MenuItem>
                                            )}
                                            {familyMembersList.length === 0 && formData.elderly_id && (
                                                <MenuItem value="" disabled>
                                                    Nessun familiare trovato
                                                </MenuItem>
                                            )}
                                            {familyMembersList.map((member) => (
                                                <MenuItem key={member.family_members_id} value={member.family_members_id}>
                                                    {member.family_members_name} {member.family_members_surname}
                                                    {member.degree_of_kinship && ` (${member.degree_of_kinship})`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {!formData.family_member_id && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            required
                                            label={t('appointments.externalFamilyMember', 'Parente esterno (non in anagrafica)')}
                                            name="external_family_member_name"
                                            value={formData.external_family_member_name}
                                            onChange={handleChange}
                                            placeholder="Es: Cugino Paolo Bianchi"
                                            helperText="Inserisci il nome del parente occasionale non registrato in anagrafica"
                                        />
                                    </Grid>
                                )}
                            </>
                        )}

                        {/* Descrizione */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                name="description"
                                label={t('appointments.description', 'Descrizione')}
                                value={formData.description}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* Note */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                name="notes"
                                label={t('appointments.notes', 'Note')}
                                value={formData.notes}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        {t('buttons.cancel', 'Annulla')}
                    </Button>
                    <Button onClick={handleSaveAppointment} variant="contained">
                        {editingId ? t('buttons.save', 'Salva') : t('buttons.add', 'Aggiungi')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AppointmentsPage;
